import { Link } from "react-router-dom";
import Nav from "@/components/Nav";

/**
 * Dedicated, fully crawlable product page. Every piece of product copy –
 * ingredients, formulation, testing – is rendered as plain semantic HTML on
 * initial load, so search engines and LLM agents can extract it without any
 * interaction.
 */
export default function ProductPage() {
  return (
    <div className="min-h-screen" style={{ background: "var(--cream)" }}>
      <Nav />

      <main
        className="mx-auto max-w-3xl px-6 md:px-10 pt-24 md:pt-32 pb-24"
        data-testid="product-page"
      >
        <p
          className="text-[0.62rem] track-sm uppercase mb-6"
          style={{ color: "var(--paper-faint)" }}
        >
          Evove Skincare
        </p>

        <h1
          className="headline text-4xl sm:text-5xl lg:text-6xl"
          data-testid="product-h1"
        >
          Skin Longevity Triple Serum
        </h1>

        <p
          className="mt-6 text-base md:text-lg leading-relaxed"
          style={{ color: "var(--paper-dim)" }}
        >
          A powerful triple-dose serum that targets barrier repair, skin
          regeneration, wrinkles, and dark spots for visibly smoother, firmer,
          more even-toned skin. Powered by{" "}
          <strong style={{ color: "var(--paper)" }}>
            Tri-Delivery&trade; Technology
          </strong>
          , a synergistic multi-pathway system designed to address multiple
          pathways of skin ageing across different skin layers.
        </p>

        <section className="mt-14" data-testid="product-what-it-does">
          <h2 className="headline text-2xl sm:text-3xl">What It Does</h2>
          <ul
            className="mt-4 flex flex-col gap-2 text-base leading-relaxed"
            style={{ color: "var(--paper-dim)" }}
          >
            <li>Barrier repair and ageing defence</li>
            <li>Cell regeneration and firmness</li>
            <li>Dark spots and radiance</li>
            <li>Skin recovery and hydration</li>
            <li>Protection against visible photoageing</li>
          </ul>
        </section>

        <section className="mt-14" data-testid="product-tridelivery">
          <h2 className="headline text-2xl sm:text-3xl">
            How Tri-Delivery Technology Works
          </h2>
          <p
            className="mt-4 text-base leading-relaxed"
            style={{ color: "var(--paper-dim)" }}
          >
            Tri-Delivery&trade; Technology is a coordinated multi-pathway
            delivery system that dispenses three separately stored serum
            formulations together in a single skincare step. Each formulation
            targets a different pillar of skin longevity — barrier support,
            cellular renewal and pigmentation — while remaining stable and
            active until the moment of use.
          </p>
        </section>

        <section className="mt-14" data-testid="product-ingredients">
          <h2 className="headline text-2xl sm:text-3xl">Active Ingredients</h2>

          <div className="mt-6">
            <h3
              className="text-base font-medium"
              style={{ color: "var(--paper)" }}
            >
              White Serum — Barrier Repair &amp; Ageing Defense
            </h3>
            <ul
              className="mt-2 pl-5 flex flex-col gap-1 list-disc"
              style={{ color: "var(--paper-dim)" }}
            >
              <li>5% Probiotic Ferment</li>
              <li>1% 5-Ceramide Complex</li>
              <li>1% Squalane</li>
              <li>1% Ectoin</li>
            </ul>
          </div>

          <div className="mt-6">
            <h3
              className="text-base font-medium"
              style={{ color: "var(--paper)" }}
            >
              Pink Serum — Cell Regeneration &amp; Firmness
            </h3>
            <ul
              className="mt-2 pl-5 flex flex-col gap-1 list-disc"
              style={{ color: "var(--paper-dim)" }}
            >
              <li>4% ExoDuo&trade; (Exosomes: Resveratrol &amp; Cica)</li>
              <li>3% Dual Peptide Complex</li>
              <li>1,000 ppm Copper Peptide (GHK-Cu)</li>
            </ul>
          </div>

          <div className="mt-6">
            <h3
              className="text-base font-medium"
              style={{ color: "var(--paper)" }}
            >
              Yellow Serum — Dark Spots &amp; Radiance
            </h3>
            <ul
              className="mt-2 pl-5 flex flex-col gap-1 list-disc"
              style={{ color: "var(--paper-dim)" }}
            >
              <li>
                2% Dual Encapsulated: Vit-C, Phytic Acid, Niacinamide,
                Azeloglicine, Bisabolol
              </li>
              <li>2% N-Acetyl Glucosamine</li>
              <li>2% Tranexamic Acid</li>
            </ul>
          </div>
        </section>

        <section className="mt-14" data-testid="product-formulated-for">
          <h2 className="headline text-2xl sm:text-3xl">Formulated For</h2>
          <p
            className="mt-4 text-base leading-relaxed"
            style={{ color: "var(--paper-dim)" }}
          >
            Breakout-prone skin, sensitive skin, all skin types, daily use
            (AM/PM). Designed with the needs and ageing patterns of Indian
            skin in mind.
          </p>
        </section>

        <section className="mt-14" data-testid="product-formulated-without">
          <h2 className="headline text-2xl sm:text-3xl">Formulated Without</h2>
          <ul
            className="mt-4 flex flex-wrap gap-x-6 gap-y-2 text-base"
            style={{ color: "var(--paper-dim)" }}
          >
            <li>Artificial Colors</li>
            <li>Fragrance</li>
            <li>Parabens</li>
            <li>Phthalates</li>
            <li>Silicone</li>
          </ul>
        </section>

        <section className="mt-14" data-testid="product-testing">
          <h2 className="headline text-2xl sm:text-3xl">
            Clinical / Testing Information
          </h2>
          <p
            className="mt-4 text-base leading-relaxed"
            style={{ color: "var(--paper-dim)" }}
          >
            Evove Skincare products are cosmetic skincare products developed
            with clinically studied actives. Product testing and dermatologist
            review information will be published on this page as it is
            finalised. Product information should not be interpreted as
            medical advice.
          </p>
        </section>

        <section className="mt-14" data-testid="product-how-to-use">
          <h2 className="headline text-2xl sm:text-3xl">How To Use</h2>
          <p
            className="mt-4 text-base leading-relaxed"
            style={{ color: "var(--paper-dim)" }}
          >
            Twist the bottle once to combine the three chambers. Dispense one
            to two pumps onto clean, dry skin, morning and evening. Follow
            with your moisturiser and, in the morning, sunscreen.
          </p>
        </section>

        <div className="mt-16 flex items-center gap-4">
          <Link
            to="/"
            className="cta inline-flex items-center gap-2"
            data-testid="back-to-home"
          >
            Join the waitlist
          </Link>
        </div>
      </main>
    </div>
  );
}
